import React, { useEffect, useState } from 'react';
import {
  AssistantRuntimeProvider,
  useLocalRuntime,
  ThreadPrimitive,
} from '@assistant-ui/react';
import {
  AGENT_SESSION_REFRESH_THRESHOLD_SECONDS,
  ensureAgentSessionFresh,
  fetchHistoryMessages,
  getStoredConversationId,
  setStoredConversationId,
  rememberThreadMessages,
  podAdapter,
} from './PodAdapter.js';
import { ConversationSwitcher } from './ConversationSwitcher.jsx';
import { DesktopPanel } from './AssistantPanel.jsx';
import {
  HubAgentContext,
  useHubAgentState,
  AgentAvatar,
  Composer,
  UserMessage,
  AssistantMessage,
} from './shared.jsx';

const IS_MAC = typeof navigator !== 'undefined'
  && /Mac|iPhone|iPad|iPod/i.test(((navigator.userAgent || '') + ' ' + (navigator.platform || '')));

function formatEventDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(+d)) return '';
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function EventCard({ event }) {
  const banner = event.banner || event.cover_image_url;
  const href = `/events/${encodeURIComponent(event.id)}`;
  return (
    <a className="hub-chat-event-card" href={href}>
      <div
        className="hub-chat-event-card-banner"
        style={banner ? { backgroundImage: `url(${banner})` } : undefined}
        aria-hidden="true"
      />
      <div className="hub-chat-event-card-body">
        <div className="hub-chat-event-card-title">{event.title}</div>
        <div className="hub-chat-event-card-meta">
          <span>{formatEventDate(event.start)}</span>
          {event.location && (
            <>
              <span aria-hidden="true">·</span>
              <span className="hub-chat-event-card-location">{event.location}</span>
            </>
          )}
        </div>
      </div>
    </a>
  );
}

function useRecentEvents(limit = 6) {
  const [events, setEvents] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetch('/api/events', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => {
        if (cancelled) return;
        const now = Date.now();
        const sorted = (Array.isArray(list) ? list : []).slice().sort((a, b) => {
          const ta = +new Date(a.start || 0);
          const tb = +new Date(b.start || 0);
          const aFuture = ta >= now;
          const bFuture = tb >= now;
          if (aFuture && !bFuture) return -1;
          if (!aFuture && bFuture) return 1;
          return aFuture ? ta - tb : tb - ta;
        });
        setEvents(sorted.slice(0, limit));
      })
      .catch(() => { if (!cancelled) setEvents([]); });
    return () => {
      cancelled = true;
    };
  }, [limit]);
  return events;
}

function EventPeekCard({ event }) {
  const banner = event.banner || event.cover_image_url;
  const href = `/events/${encodeURIComponent(event.id)}`;
  return (
    <a className="hub-event-peek-card" href={href} title={event.title || ''}>
      <div
        className="hub-event-peek-card-banner"
        style={banner ? { backgroundImage: `url(${banner})` } : undefined}
        aria-hidden="true"
      />
      <div className="hub-event-peek-card-body">
        <div className="hub-event-peek-card-title">{event.title}</div>
        <div className="hub-event-peek-card-date">{formatEventDate(event.start)}</div>
      </div>
    </a>
  );
}

function useLatestThreadId() {
  const [latest, setLatest] = useState(undefined);
  useEffect(() => {
    let cancelled = false;
    fetch('/api/agent/local-responses/conversations?limit=1&offset=0', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return;
        const top = (data && Array.isArray(data.conversations) && data.conversations[0]) || null;
        setLatest(top ? String(top.id) : null);
      })
      .catch(() => { if (!cancelled) setLatest(null); });
    return () => { cancelled = true; };
  }, []);
  return latest;
}

function getActiveThreadIdFromUrl() {
  if (typeof window === 'undefined') return null;
  try {
    return new URL(window.location.href).searchParams.get('thread') || null;
  } catch {
    return null;
  }
}

function ShowAllEventsCard() {
  return (
    <a className="hub-event-peek-card hub-event-peek-card-all" href="/events" title="All events">
      <div className="hub-event-peek-card-banner hub-event-peek-card-all-banner" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="5" width="18" height="16" rx="2"></rect>
          <line x1="3" y1="10" x2="21" y2="10"></line>
          <line x1="8" y1="3" x2="8" y2="7"></line>
          <line x1="16" y1="3" x2="16" y2="7"></line>
        </svg>
      </div>
      <div className="hub-event-peek-card-body">
        <div className="hub-event-peek-card-title">Show all</div>
        <div className="hub-event-peek-card-date">View events</div>
      </div>
    </a>
  );
}

function EventPeekStrip() {
  const events = useRecentEvents(2);
  const latestId = useLatestThreadId();
  // Only render the strip when the user is on the latest thread (or a fresh chat
  // with no ?thread param). Hide on older threads.
  if (latestId === undefined) return null; // still loading
  const active = getActiveThreadIdFromUrl();
  if (active && latestId && active !== latestId) return null;
  if (events === null) return null; // events still loading
  const realCards = events.slice(0, 2);
  return (
    <div className="hub-event-peek-strip" aria-label="Recent events">
      {realCards.map((e) => (
        <EventPeekCard key={e.id} event={e} />
      ))}
      <ShowAllEventsCard />
    </div>
  );
}

function RecentEvents() {
  const events = useRecentEvents(6);

  // Loading: render skeleton cards
  if (events === null) {
    return (
      <section className="hub-chat-events">
        <h3>Latest events</h3>
        <div className="hub-chat-events-grid">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="hub-chat-event-card hub-chat-event-card-skel" aria-hidden="true">
              <div className="hub-chat-event-card-banner hub-skel-shimmer" />
              <div className="hub-chat-event-card-body">
                <div className="hub-skel-bar hub-skel-bar-title" />
                <div className="hub-skel-bar hub-skel-bar-meta" />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (!events.length) return null;
  return (
    <section className="hub-chat-events">
      <h3>Latest events</h3>
      <div className="hub-chat-events-grid">
        {events.map((e) => (
          <EventCard key={e.id} event={e} />
        ))}
      </div>
    </section>
  );
}

function ChatBody({ agent, runtime, desktopOpen, onCloseDesktop }) {
  return (
    <div className={`hub-chat-shell ${desktopOpen ? 'has-desktop' : ''}`}>
      {desktopOpen && <DesktopPanel onClose={onCloseDesktop} />}
      <ThreadPrimitive.Root className="hub-chat-root">
        <ConversationSwitcher runtime={runtime} variant="page" />
        <ThreadPrimitive.Empty>
          <div className="hub-chat-hero">
            <AgentAvatar agent={agent} className="hub-msg-avatar-xl" />
            <h1>What do you want to do today?</h1>
            <p className="hub-chat-hero-sub">
              Ask {agent?.display_name || 'your agent'} anything, or pick something below.
            </p>
          </div>
        </ThreadPrimitive.Empty>
        <ThreadPrimitive.Viewport className="hub-chat-viewport">
          <ThreadPrimitive.Messages
            components={{ UserMessage, AssistantMessage }}
          />
        </ThreadPrimitive.Viewport>
        <div className="hub-chat-composer-band">
          <img
            className="hub-chat-planter"
            src="/planthub.png"
            alt=""
            aria-hidden="true"
          />
          <ThreadPrimitive.If empty={false}>
            <EventPeekStrip />
          </ThreadPrimitive.If>
          <div className="hub-chat-composer-wrap">
            <Composer placeholder="Message your agent…" />
          </div>
          <button
            type="button"
            className={`hub-chat-composer-shortcut${desktopOpen ? ' is-open' : ''}`}
            title={desktopOpen ? 'Close Desktop' : 'Open Desktop'}
            onClick={() => {
              window.dispatchEvent(new CustomEvent('hub:assistant-action', { detail: { type: 'desktop_toggle' } }));
            }}
          >
            <span className="hub-chat-composer-shortcut-keys">
              <kbd>{IS_MAC ? '⌘' : 'Ctrl'}</kbd>
              <kbd>U</kbd>
            </span>
            <span className="hub-chat-composer-shortcut-text">{desktopOpen ? 'to close Desktop' : 'to open Desktop'}</span>
          </button>
        </div>
        <ThreadPrimitive.Empty>
          <RecentEvents />
        </ThreadPrimitive.Empty>
      </ThreadPrimitive.Root>
    </div>
  );
}

export function ChatPage() {
  const runtime = useLocalRuntime(podAdapter);
  const { agent, loading: agentLoading, avatarReady } = useHubAgentState({ preloadAvatar: true });
  const [sessionReady, setSessionReady] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setSessionReady(false);
    ensureAgentSessionFresh(controller.signal, {
      minExpiresInSeconds: AGENT_SESSION_REFRESH_THRESHOLD_SECONDS,
      redirect: false,
    }).then((ok) => {
      // Home chat should not kick off a login redirect before the user sends.
      // The send path probes again and reports expiry inline when needed.
      if (!controller.signal.aborted) setSessionReady(true);
    }).catch(() => {
      if (!controller.signal.aborted) setSessionReady(true);
    });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    // URL ?thread=<id> wins over sessionStorage so sidebar links / shared URLs
    // load the conversation the user actually clicked. Sync the chosen id into
    // sessionStorage so subsequent runtime calls (sends, switcher) agree.
    let urlThread = '';
    try {
      urlThread = new URL(window.location.href).searchParams.get('thread') || '';
    } catch {}
    const id = urlThread || getStoredConversationId();
    if (!id) return undefined;
    if (urlThread && urlThread !== getStoredConversationId()) {
      setStoredConversationId(urlThread);
    }
    const controller = new AbortController();
    fetchHistoryMessages(id, controller.signal).then((msgs) => {
      if (controller.signal.aborted || !msgs.length) return;
      rememberThreadMessages(msgs);
      try { runtime.thread?.reset(msgs); } catch {}
    });
    return () => controller.abort();
  }, [runtime]);

  // Cmd/Ctrl+I focuses composer. Cmd/Ctrl+U toggles the pod desktop.
  useEffect(() => {
    const setDesktopState = (state) => setDesktopOpen((v) => state === 'toggle' ? !v : state === 'open');
    const onKey = (e) => {
      const k = (e.key || '').toLowerCase();
      if (k === 'u' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setDesktopState('toggle');
      } else if (k === 'i' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        const el = document.querySelector('.hub-chat-composer-wrap .hub-composer-input');
        el && el.focus();
      }
    };
    const onAssistantAction = (e) => {
      if (e?.detail?.type === 'desktop_toggle') setDesktopState('toggle');
      if (e?.detail?.type === 'desktop_open') setDesktopState('open');
      if (e?.detail?.type === 'desktop_close') setDesktopState('close');
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('hub:assistant-action', onAssistantAction);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('hub:assistant-action', onAssistantAction);
    };
  }, []);

  const ready = sessionReady && !agentLoading && avatarReady;

  return (
    <HubAgentContext.Provider value={agent}>
      <AssistantRuntimeProvider runtime={runtime}>
        {ready ? (
          <ChatBody
            agent={agent}
            runtime={runtime}
            desktopOpen={desktopOpen}
            onCloseDesktop={() => setDesktopOpen(false)}
          />
        ) : (
          <div className="hub-chat-shell">
            <div className="hub-chat-skeleton" role="status" aria-busy="true" aria-label="Loading chat">
              <div className="hub-chat-skeleton-switcher hub-skel-shimmer" />
              <div className="hub-chat-skeleton-msgs">
                <div className="hub-chat-skeleton-msg hub-chat-skeleton-msg-in">
                  <div className="hub-chat-skeleton-bubble hub-skel-shimmer" style={{ width: '64%' }} />
                </div>
                <div className="hub-chat-skeleton-msg hub-chat-skeleton-msg-out">
                  <div className="hub-chat-skeleton-bubble hub-skel-shimmer" style={{ width: '48%' }} />
                </div>
                <div className="hub-chat-skeleton-msg hub-chat-skeleton-msg-in">
                  <div className="hub-chat-skeleton-bubble hub-skel-shimmer" style={{ width: '72%' }} />
                </div>
                <div className="hub-chat-skeleton-msg hub-chat-skeleton-msg-out">
                  <div className="hub-chat-skeleton-bubble hub-skel-shimmer" style={{ width: '38%' }} />
                </div>
                <div className="hub-chat-skeleton-msg hub-chat-skeleton-msg-in">
                  <div className="hub-chat-skeleton-bubble hub-skel-shimmer" style={{ width: '54%' }} />
                </div>
              </div>
              <div className="hub-chat-skeleton-composer hub-skel-shimmer" />
              <span className="hub-chat-skeleton-sr">Preparing your agent…</span>
            </div>
          </div>
        )}
      </AssistantRuntimeProvider>
    </HubAgentContext.Provider>
  );
}
